import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { canFeature } from '@/lib/perms';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Save, ChevronDown, ChevronRight, BookOpen, CheckCircle, LayoutTemplate, RefreshCw } from 'lucide-react';
import { useConfirm } from '@/components/ConfirmDialog';

const FieldInput = ({ field, value, onChange }) => {
  const testid = `brief-field-${field.id}`;
  if (field.type === 'textarea')
    return <textarea value={value || ''} onChange={e => onChange(e.target.value)} placeholder={field.placeholder}
      className="w-full h-20 px-3 py-2 text-sm border border-[#D4D0C8] rounded-lg resize-none" data-testid={testid} />;
  if (field.type === 'number')
    return <Input type="number" value={value ?? ''} onChange={e => onChange(e.target.value === '' ? '' : Number(e.target.value))}
      placeholder={field.placeholder} className="h-9 rounded-lg border-[#D4D0C8] text-sm" data-testid={testid} />;
  if (field.type === 'date')
    return <Input type="date" value={value || ''} onChange={e => onChange(e.target.value)}
      className="h-9 rounded-lg border-[#D4D0C8] text-sm" data-testid={testid} />;
  if (field.type === 'toggle')
    return (
      <button onClick={() => onChange(!value)} data-testid={testid}
        className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${value ? 'bg-[#3A8C8C] text-white border-[#3A8C8C]' : 'bg-white text-[#6B7280] border-[#D4D0C8]'}`}>
        {value ? 'Yes' : 'No'}
      </button>);
  if (field.type === 'dropdown')
    return (
      <Select value={value || ''} onValueChange={onChange}>
        <SelectTrigger className="h-9 rounded-lg border-[#D4D0C8] text-sm" data-testid={testid}><SelectValue placeholder="Select..." /></SelectTrigger>
        <SelectContent>{(field.options || []).map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select>);
  if (field.type === 'multiselect') {
    const arr = Array.isArray(value) ? value : [];
    return (
      <div className="flex flex-wrap gap-1.5 mt-1" data-testid={testid}>
        {(field.options || []).map(o => {
          const sel = arr.includes(o);
          return <button key={o} onClick={() => onChange(sel ? arr.filter(x => x !== o) : [...arr, o])}
            className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${sel ? 'bg-[#3A8C8C] text-white border-[#3A8C8C]' : 'bg-white text-[#6B7280] border-[#D4D0C8] hover:border-[#3A8C8C]'}`}>{o}</button>;
        })}
      </div>);
  }
  return <Input value={value || ''} onChange={e => onChange(e.target.value)} placeholder={field.placeholder}
    className="h-9 rounded-lg border-[#D4D0C8] text-sm" data-testid={testid} />;
};

export default function ClientBriefTab({ projectId }) {
  const { user } = useAuth();
  const [confirm, confirmDialog] = useConfirm();
  const isAdmin = canFeature(user, 'manageData');
  const [loading, setLoading] = useState(true);
  const [brief, setBrief] = useState(null);
  const [values, setValues] = useState({});
  const [expanded, setExpanded] = useState({});
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [pickCategory, setPickCategory] = useState('Residential');
  const [pickTemplateId, setPickTemplateId] = useState('');
  const [suggested, setSuggested] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/projects/${projectId}/brief-doc`);
      setBrief(res.data.brief);
      setValues(res.data.brief?.fieldValues || {});
      setSuggested(res.data.suggestedTemplateId);
      setPickCategory(res.data.suggestedCategory || 'Residential');
      setPickTemplateId(res.data.suggestedTemplateId || '');
      if (res.data.brief?.sections?.length) setExpanded({ [res.data.brief.sections[0].id]: true });
    } catch { toast.error('Failed to load brief'); }
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const loadTemplates = useCallback(async (cat) => {
    if (!isAdmin) return;
    try { const r = await api.get(`/brief-templates?category=${cat}`); setTemplates(r.data); }
    catch { setTemplates([]); }
  }, [isAdmin]);

  useEffect(() => { if (isAdmin && (!brief)) loadTemplates(pickCategory); }, [isAdmin, brief, pickCategory, loadTemplates]);

  const applyTemplate = async (overwrite) => {
    if (!pickTemplateId) { toast.error('Pick a template'); return; }
    if (brief && !overwrite && !(await confirm({ title: 'Replace current brief?', message: 'This replaces the current brief and its filled client data.', confirmLabel: 'Replace' }))) return;
    try {
      const res = await api.post(`/projects/${projectId}/brief-doc/apply`, { templateId: pickTemplateId });
      setBrief(res.data); setValues(res.data.fieldValues || {});
      setExpanded({ [res.data.sections[0]?.id]: true });
      toast.success('Template applied');
    } catch (e) { toast.error(e.response?.data?.detail || 'Apply failed'); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/projects/${projectId}/brief-doc/values`, { fieldValues: values });
      setBrief(res.data); toast.success('Brief saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const filled = (v) => v !== '' && v !== null && v !== undefined && (Array.isArray(v) ? v.length > 0 : (typeof v === 'boolean' ? v : true));

  if (loading) return <div className="flex items-center justify-center h-32"><div className="w-5 h-5 border-2 border-[#3A8C8C] border-t-transparent rounded-full animate-spin" /></div>;

  // Empty state → template picker
  if (!brief) {
    return (
      <div className="glass-card rounded-2xl p-8 text-center" data-testid="brief-empty-state">
        <LayoutTemplate size={32} strokeWidth={1.5} className="text-[#3A8C8C] mx-auto mb-3" />
        <h3 className="text-base font-['Manrope'] font-semibold text-[#2C2C2E]">No brief yet</h3>
        <p className="text-sm text-[#6B7280] mt-1 mb-5">{isAdmin ? 'Apply a brief template to start capturing this client\'s requirements.' : 'The project admin needs to apply a brief template.'}</p>
        {isAdmin && (
          <div className="max-w-md mx-auto space-y-3">
            <div className="flex gap-2">
              <Select value={pickCategory} onValueChange={(v) => { setPickCategory(v); loadTemplates(v); setPickTemplateId(''); }}>
                <SelectTrigger className="rounded-lg border-[#D4D0C8]" data-testid="brief-pick-category"><SelectValue /></SelectTrigger>
                <SelectContent>{['Residential', 'Commercial', 'Retail', 'Custom'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={pickTemplateId} onValueChange={setPickTemplateId}>
                <SelectTrigger className="rounded-lg border-[#D4D0C8]" data-testid="brief-pick-template"><SelectValue placeholder="Choose template" /></SelectTrigger>
                <SelectContent>{templates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}{t.isDefault ? ' (Default)' : ''}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <button onClick={() => applyTemplate(true)} disabled={!pickTemplateId} data-testid="brief-apply-btn"
              className="px-5 py-2 rounded-lg bg-[#3A8C8C] text-white text-sm font-medium hover:bg-[#2E7A7A] transition-colors disabled:opacity-50">
              Apply Template
            </button>
          </div>
        )}
      </div>
    );
  }

  const completion = brief.overallPercent || 0;

  return (
    <div className="space-y-4" data-testid="brief-questionnaire">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="section-heading text-sm font-semibold font-['Manrope'] flex items-center gap-2">
            <BookOpen size={16} strokeWidth={1.5} className="text-[#3A8C8C]" /> {brief.templateName}
          </h3>
          <div className="flex items-center gap-2 mt-1">
            <div className="flex-1 h-1.5 bg-[#E8E6E1] rounded-full max-w-32 overflow-hidden">
              <div className="h-full bg-[#3A8C8C] rounded-full transition-all duration-300" style={{ width: `${completion}%` }} />
            </div>
            <span className="text-[10px] text-[#6B7280]" data-testid="brief-overall-percent">{completion}% complete</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <button onClick={() => { loadTemplates(pickCategory); setBrief(null); }} data-testid="brief-change-template-btn"
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#E8E6E1] text-[#2C2C2E] text-xs font-medium hover:bg-[#D4D0C8] transition-colors">
              <RefreshCw size={13} strokeWidth={1.5} /> Change Template
            </button>
          )}
          <button onClick={save} disabled={saving} data-testid="save-brief-btn"
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#3A8C8C] text-white text-xs font-medium hover:bg-[#2E7A7A] transition-colors disabled:opacity-50">
            <Save size={14} strokeWidth={1.5} /> {saving ? 'Saving...' : 'Save Brief'}
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {brief.sections.map((section, sIdx) => {
          const isExp = expanded[section.id];
          const secFilled = section.fields.filter(f => filled(values[f.id])).length;
          const secTotal = section.fields.length;
          return (
            <div key={section.id} className="glass-card rounded-xl overflow-hidden" data-testid={`brief-section-${sIdx}`}>
              <button onClick={() => setExpanded(p => ({ ...p, [section.id]: !p[section.id] }))}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-[#FAFAF8]/50 transition-colors">
                <div className="flex items-center gap-2">
                  {isExp ? <ChevronDown size={16} className="text-[#6B7280]" /> : <ChevronRight size={16} className="text-[#6B7280]" />}
                  <span className="text-sm font-medium text-[#2C2C2E]">{section.title}</span>
                </div>
                <div className="flex items-center gap-2">
                  {secFilled === secTotal && secTotal > 0 && <CheckCircle size={14} strokeWidth={1.5} className="text-[#7DA68A]" />}
                  <Badge className="bg-[#E8E6E1] text-[#6B7280] text-[10px]">{secFilled}/{secTotal}</Badge>
                </div>
              </button>
              {isExp && (
                <div className="px-4 pb-4 pt-1 grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-[#E8E6E1]">
                  {section.fields.map(field => (
                    <div key={field.id} className={['textarea', 'multiselect'].includes(field.type) ? 'md:col-span-2' : ''}>
                      <Label className="text-xs text-[#6B7280] uppercase mb-1 block">{field.label}{field.required && <span className="text-[#D94F4F]"> *</span>}</Label>
                      <FieldInput field={field} value={values[field.id]} onChange={(v) => setValues(prev => ({ ...prev, [field.id]: v }))} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {confirmDialog}
    </div>
  );
}
